import { CITIES, project, ROADS, type CityId } from "./sp-map.ts";

/**
 * The trips on the landing page's map. Nothing here is real data: vehicles pick weighted random trips
 * over the highway sketch in sp-map.ts and drive them, one after the other, starting where the last one
 * ended. Holds no DOM, so LiveMap.tsx only draws what `step` leaves on the vehicles.
 */
export type VehicleKind = "car" | "moto";
export type Point = { x: number; y: number };

const SAMPLES_PER_EDGE = 8;

type Edge = { to: CityId; length: number; points: Point[] };

const dist = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);

/** Catmull-Rom through a highway's cities, so it bends instead of kinking at each one. */
function curve(cities: readonly CityId[]): Point[][] {
  const at = cities.map((c) => project(CITIES[c].at));
  return at.slice(0, -1).map((p1, i) => {
    const p0 = at[i - 1] ?? p1;
    const p2 = at[i + 1]!;
    const p3 = at[i + 2] ?? p2;
    return Array.from({ length: SAMPLES_PER_EDGE + 1 }, (_, n) => {
      const t = n / SAMPLES_PER_EDGE;
      const blend = (a: number, b: number, c: number, d: number) =>
        0.5 * (2 * b + (c - a) * t + (2 * a - 5 * b + 4 * c - d) * t * t + (3 * b - a - 3 * c + d) * t * t * t);
      return { x: blend(p0.x, p1.x, p2.x, p3.x), y: blend(p0.y, p1.y, p2.y, p3.y) };
    });
  });
}

const polylineLength = (points: Point[]) => points.slice(1).reduce((sum, p, i) => sum + dist(points[i]!, p), 0);
const pathOf = (points: Point[]) => `M${points.map((p) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join("L")}`;

const CURVES = ROADS.map(curve);
/** One path per highway, for drawing the network under the trips. */
export const ROAD_PATHS = CURVES.map((segments) => pathOf(segments.flatMap((s, i) => (i === 0 ? s : s.slice(1)))));

export const GRAPH = (() => {
  const graph = new Map<CityId, Edge[]>();
  const link = (from: CityId, to: CityId, points: Point[]) => {
    const edges = graph.get(from) ?? [];
    edges.push({ to, points, length: polylineLength(points) });
    graph.set(from, edges);
  };
  ROADS.forEach((road, r) =>
    CURVES[r]!.forEach((points, i) => {
      link(road[i]!, road[i + 1]!, points);
      link(road[i + 1]!, road[i]!, [...points].reverse());
    }),
  );
  return graph;
})();

const CITY_IDS = [...GRAPH.keys()];

type Paths = { cost: Map<CityId, number>; via: Map<CityId, { from: CityId; edge: Edge }> };
const pathsCache = new Map<CityId, Paths>();

/** Dijkstra from one city. The graph is about sixty cities, so a scan beats a heap. */
function pathsFrom(source: CityId): Paths {
  const cached = pathsCache.get(source);
  if (cached) return cached;
  const cost = new Map<CityId, number>([[source, 0]]);
  const via: Paths["via"] = new Map();
  const open = new Set<CityId>([source]);
  const done = new Set<CityId>();
  while (open.size > 0) {
    let city = open.values().next().value as CityId;
    for (const c of open) if (cost.get(c)! < cost.get(city)!) city = c;
    open.delete(city);
    done.add(city);
    for (const edge of GRAPH.get(city) ?? []) {
      if (done.has(edge.to)) continue;
      const total = cost.get(city)! + edge.length;
      if (total < (cost.get(edge.to) ?? Infinity)) {
        cost.set(edge.to, total);
        via.set(edge.to, { from: city, edge });
        open.add(edge.to);
      }
    }
  }
  const paths = { cost, via };
  pathsCache.set(source, paths);
  return paths;
}

export type Trip = {
  from: CityId;
  to: CityId;
  points: Point[];
  /** Distance along the route at each point. */
  along: number[];
  length: number;
  path: string;
};

const tripCache = new Map<string, Trip>();

export function tripBetween(from: CityId, to: CityId): Trip {
  const key = `${from}|${to}`;
  const cached = tripCache.get(key);
  if (cached) return cached;
  const { via } = pathsFrom(from);
  const legs: Point[][] = [];
  for (let city = to; city !== from; ) {
    const step = via.get(city)!;
    legs.unshift(step.edge.points);
    city = step.from;
  }
  const points = legs.flatMap((leg, i) => (i === 0 ? leg : leg.slice(1)));
  const along = [0];
  for (let i = 1; i < points.length; i++) along.push(along[i - 1]! + dist(points[i - 1]!, points[i]!));
  const trip = { from, to, points, along, length: along.at(-1)!, path: pathOf(points) };
  tripCache.set(key, trip);
  return trip;
}

export function pointAlong(trip: Trip, s: number): Point {
  const { points, along } = trip;
  let low = 0;
  let high = along.length - 1;
  while (high - low > 1) {
    const mid = (low + high) >> 1;
    if (along[mid]! <= s) low = mid;
    else high = mid;
  }
  const span = along[high]! - along[low]! || 1;
  const t = Math.min(1, Math.max(0, (s - along[low]!) / span));
  return { x: points[low]!.x + (points[high]!.x - points[low]!.x) * t, y: points[low]!.y + (points[high]!.y - points[low]!.y) * t };
}

/** Motos make the short hops, cars also take the highway between cities. */
const TRIP_RANGE: Record<VehicleKind, readonly [min: number, max: number]> = { car: [90, 500], moto: [30, 190] };
const TOP_SPEED: Record<VehicleKind, number> = { car: 62, moto: 72 };
const RAMP = 34; // distance over which a vehicle gets up to speed and slows back down
const CREEP = 0.16; // share of top speed at the very start and end of a trip
const ARRIVED_FOR = 1.1;

export type Vehicle = {
  kind: VehicleKind;
  phase: "waiting" | "driving" | "arrived";
  city: CityId;
  trip: Trip;
  /** Goes up with every new trip, so whoever draws knows when to swap the route. */
  tripNumber: number;
  /** Distance driven on this trip. */
  s: number;
  x: number;
  y: number;
  /** Degrees clockwise from north. */
  heading: number;
  timer: number;
  speed: number;
};

/** Small seeded generator (mulberry32): the tests get the same traffic every run. */
function random(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function createTraffic({ cars, motos, seed = (Math.random() * 2 ** 31) | 0 }: { cars: number; motos: number; seed?: number }) {
  const rand = random(seed);

  const pickWeighted = (ids: CityId[]) => {
    let roll = rand() * ids.reduce((sum, c) => sum + CITIES[c].weight, 0);
    for (const c of ids) {
      roll -= CITIES[c].weight;
      if (roll <= 0) return c;
    }
    return ids[ids.length - 1]!;
  };

  const nextTrip = (kind: VehicleKind, from: CityId) => {
    const { cost } = pathsFrom(from);
    const [min, max] = TRIP_RANGE[kind];
    const inRange = CITY_IDS.filter((c) => c !== from && cost.get(c)! >= min && cost.get(c)! <= max);
    // A far-off town can have nothing in range: then the nearest city gets it moving again.
    const nearest = () => CITY_IDS.filter((c) => c !== from).sort((a, b) => cost.get(a)! - cost.get(b)!)[0]!;
    return tripBetween(from, inRange.length > 0 ? pickWeighted(inRange) : nearest());
  };

  const begin = (v: Vehicle, wait: number) => {
    v.trip = nextTrip(v.kind, v.city);
    v.tripNumber++;
    v.phase = "waiting";
    v.s = 0;
    v.timer = wait;
    v.speed = TOP_SPEED[v.kind] * (0.85 + rand() * 0.3);
  };

  const headingOn = (trip: Trip, s: number) => {
    const here = pointAlong(trip, s);
    const ahead = pointAlong(trip, Math.min(trip.length, s + 5));
    return here.x === ahead.x && here.y === ahead.y ? null : (Math.atan2(ahead.x - here.x, here.y - ahead.y) * 180) / Math.PI;
  };

  const vehicles = Array.from({ length: cars + motos }, (_, i): Vehicle => {
    const kind: VehicleKind = i < cars ? "car" : "moto";
    const city = pickWeighted(CITY_IDS);
    const v: Vehicle = { kind, phase: "waiting", city, trip: tripBetween(city, GRAPH.get(city)![0]!.to), tripNumber: 0, s: 0, ...project(CITIES[city].at), heading: rand() * 360, timer: 0, speed: 0 };
    begin(v, rand() * 8);
    return v;
  });

  const step = (dt: number) => {
    for (const v of vehicles) {
      if (v.phase === "waiting") {
        v.timer -= dt;
        if (v.timer <= 0) v.phase = "driving";
      } else if (v.phase === "driving") {
        const edge = Math.min(v.s, v.trip.length - v.s);
        v.s += v.speed * Math.min(1, CREEP + edge / RAMP) * dt;
        if (v.s >= v.trip.length) {
          v.s = v.trip.length;
          v.phase = "arrived";
          v.timer = ARRIVED_FOR;
          v.city = v.trip.to;
        }
      } else {
        v.timer -= dt;
        if (v.timer <= 0) begin(v, 0.5 + rand() * 1.2);
      }
      const at = pointAlong(v.trip, v.s);
      v.x = at.x;
      v.y = at.y;
      const target = v.phase === "arrived" ? null : headingOn(v.trip, v.s);
      if (target !== null) {
        const turn = ((target - v.heading + 540) % 360) - 180;
        v.heading += turn * (1 - Math.exp(-dt * 9));
      }
    }
  };

  return { vehicles, step };
}
